// ── Shared project cache (C1395) ──
//
// Before this, only board.js fetched the project (its own inline `api.get('/projects/' +
// id).catch(...)` leg, C1234) — every other page that needs task_group_label
// (sprint-planning.js, task-detail.js) either hardcoded "Sprint" or would have needed its
// own duplicate fetch. Same memoize-per-id discipline as lib/statuses.js's loadStatuses():
// a page's Promise.all calls this once, task-detail's drawer (opened from several pages
// per session) rides the same cached promise instead of re-fetching.
//
// Settings-page saves (project info, language, appearance, VCS, sprints toggle, sprint
// duration, memory sync-as-you-go toggle, objective-grouping toggle) call invalidateProject()
// right after a successful PATCH so the NEXT page visit re-fetches —
// this module has no live-update path of its own (unlike layout.js's projectCache, which
// a same-page save writes into directly).

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { projectWithPermissions } from './permissions.js';

const _cache = new Map(); // projectId -> Promise<Project|null>
let cacheUserId;

// Fetches `GET /projects/:id`. Fails open (null) on error — a hiccup here shouldn't break
// a page that only needs this for a display noun, matching board.js's original
// `.catch(() => ({ project: null }))` leg it replaces.
export async function loadProject(projectId) {
  const userId = getUser()?.id;
  if (cacheUserId !== userId) {
    _cache.clear();
    cacheUserId = userId;
  }
  projectId = String(projectId);
  if (!_cache.has(projectId)) {
    _cache.set(projectId, api.get(`/projects/${projectId}`)
      .then(projectWithPermissions)
      .catch(() => null));
  }
  return _cache.get(projectId);
}

// Drop this project's cached entry — call after any successful `PATCH /projects/:id` so
// the next loadProject() call re-fetches instead of serving the pre-save value.
export function invalidateProject(projectId) {
  _cache.delete(String(projectId));
}
